import { createContext, useContext, useEffect, useMemo } from 'react';
import { cssVariablesToString, themes, toCssVariables, type Theme } from '@greasemap/design';

interface ThemeContextValue {
  readonly theme: Theme;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STYLE_ELEMENT_ID = 'greasemap-theme';

/**
 * Applies a theme to the document as CSS custom properties.
 *
 * Writing variables once on the root means the whole palette is a single style element, rather than
 * re-rendering every component that happens to mention a colour.
 *
 * @param theme - The theme to apply.
 */
function applyTheme(theme: Theme): void {
  let style = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;

  if (style === null) {
    style = document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    document.head.appendChild(style);
  }

  style.textContent = `:root {\n${cssVariablesToString(toCssVariables(theme))}\n}`;
  document.documentElement.dataset['theme'] = theme.name;
  document.documentElement.style.colorScheme = theme.name;
}

/**
 * Provides the application theme and applies it to the document.
 *
 * Greasemap web ships a single dark theme: it is the standard interface, there is no light mode and no
 * user-facing switch. The provider applies dark on mount and exposes it to the tree for any component
 * that needs to read a token in JavaScript.
 *
 * @param props - Component props.
 * @param props.children - The application tree.
 * @returns The provider element.
 */
export function ThemeProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const theme = themes.dark;

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const value = useMemo(() => ({ theme }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Reads the active theme.
 *
 * @returns The theme context.
 * @throws {Error} Called outside a {@link ThemeProvider}.
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (context === null) {
    throw new Error('useTheme must be used inside a ThemeProvider.');
  }

  return context;
}
